import { IDocument } from 'types';
import { useCallback, useState, useMemo } from 'react';
import { Button, IconButton, Tooltip, Box, Typography } from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLocale, useResourceDelete, useComponentVisibility, useResourceUpdate } from '@koopajs/react';
import { TrackEvent } from '@koopajs/app';
import { SystemStyleObject } from '@mui/system';
import { RenderDocumentIcon } from 'components/RenderDocumentIcon';
import { useIsDocumentDownloadBlocked } from 'components/hooks/useIsDocumentDownloadBlocked';
import { FormControllerTemp } from 'components/temp/FormControllerTemp';
import { TextField } from 'components/temp/TextFieldTemp';

interface IDocumentProps {
  document: IDocument;
  isDeletable?: boolean;
  isTitleEditable?: boolean;
  isViewPrint?: boolean;
  path: string;
  setIsProcessing?: (isProcessing: boolean) => void;
  isProcessing: boolean;
  sx?: SystemStyleObject;
  meetingPdfDocuments?: IDocument[];
  isAnnotatable?: boolean;
  deleteConfirmationDialogKey?: string;
}

export const Document: React.FC<IDocumentProps> = (props) => {
  const {
    isDeletable,
    isTitleEditable,
    document,
    isViewPrint,
    path,
    setIsProcessing,
    isProcessing: isProcessingOtherDocuments,
    sx,
    meetingPdfDocuments,
    isAnnotatable,
    deleteConfirmationDialogKey
  } = props;

  const isUrl = document?.attachedFile?.format === 'url';
  const isPdf = document?.attachedFile?.format === 'application/pdf';
  const isNotPdfOrUrl = !isUrl && !isPdf;

  const documentViewDialog = useComponentVisibility('documentPdfDialogView');
  const documentAnnotateDialog = useComponentVisibility('boardMemberViewDocumentDialog');
  const confirmDeleteDocumentDialog = useComponentVisibility(deleteConfirmationDialogKey || '');

  const showDeleteConfirmationDialog = deleteConfirmationDialogKey && isPdf;

  const handleConfirmDeleteDocument = useCallback(() => {
    confirmDeleteDocumentDialog.setVisibleWithContext({
      documentId: document.id,
      path: path
    });
  }, [document.id, path, confirmDeleteDocumentDialog]);

  const [isEditingTitle, setIsEditingTitle] = useState(false);

  const keyPrefix = 'Components.DocumentList';
  const { t } = useLocale();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const deleteDocument = useResourceDelete({
    path: path,
    id: document.id
  });

  const updateDocument = useResourceUpdate({
    path: path,
    id: document.id
  });
  // allow annotation of documents only in Edit Meeting and View Meeting
  const handleOpenDialog = useCallback(() => {
    if ((meetingPdfDocuments && meetingPdfDocuments.length > 0) || isAnnotatable) {
      documentAnnotateDialog.setVisibleWithContext({
        documentId: document.id,
        pdfDocuments: meetingPdfDocuments
      });
    } else {
      documentViewDialog.setVisibleWithContext({ documentId: document.id });
    }
  }, [document.id, JSON.stringify(meetingPdfDocuments), isAnnotatable]);

  const handleCloseSnackbar = useCallback(
    (key: any) => {
      return () => {
        closeSnackbar(key);
      };
    },
    [closeSnackbar]
  );

  const handleDeleteDocument = useCallback(async () => {
    if (setIsProcessing) setIsProcessing(true);

    const res = await deleteDocument.deleteResource();

    if (res) {
      enqueueSnackbar(t(keyPrefix + '.snackbarDeleteSuccess'), {
        variant: 'success',
        action: (key: any) => (
          <IconButton size="small" onClick={handleCloseSnackbar(key)}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        )
      });
    } else {
      enqueueSnackbar(t(keyPrefix + '.snackbarDeleteFail'), {
        variant: 'error',
        action: (key: any) => (
          <IconButton size="small" onClick={handleCloseSnackbar(key)}>
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>
        )
      });

      TrackEvent('document-delete-fail');
    }
    if (setIsProcessing) setIsProcessing(false);
  }, [deleteDocument]);

  const isDocumentDownloadBlocked = useIsDocumentDownloadBlocked();

  const { fileExtension, fileNameWithoutExtension } = useMemo(() => {
    const name = document?.attachedFile?.name ?? document?.attachedFile?.uri ?? '';
    const idx = name.lastIndexOf('.');
    return {
      fileName: name,
      fileExtension: idx > -1 ? name.slice(idx) : '',
      fileNameWithoutExtension: idx > -1 ? name.slice(0, idx) : name
    };
  }, [document]);

  const onSubmit = useCallback(
    async (formData: object): Promise<boolean> => {
      const formDataTyped = formData as { documentTitle: string };
      const payload = { attachedFile: { name: formDataTyped.documentTitle + fileExtension } };

      const res = await updateDocument.updateResource(payload);

      if (res) {
        setIsEditingTitle(false);
      } else {
        enqueueSnackbar(t(keyPrefix + '.snackbarUpdateFail'), {
          variant: 'error',
          action: (key: any) => (
            <IconButton size="small" onClick={handleCloseSnackbar(key)}>
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          )
        });
        TrackEvent('document-update-fail');
      }

      return res;
    },
    [updateDocument, enqueueSnackbar, t, keyPrefix, handleCloseSnackbar, TrackEvent, fileExtension]
  );

  return (
    <>
      <Box
        sx={{
          maxWidth: '100%',
          ...sx
        }}
        data-cy="document-view-button-container"
      >
        {isTitleEditable && isEditingTitle ? (
          <FormControllerTemp
            onSubmit={onSubmit}
            onSubmitSuccessResetType="FORM_PAYLOAD"
            defaultValues={{ documentTitle: fileNameWithoutExtension }}
            sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
          >
            <TextField
              name="documentTitle"
              i18n={{
                keyPrefix:
                  document.attachedFile.format === 'url'
                    ? 'Components.DocumentList.Form.linkTitleField'
                    : 'Components.DocumentList.Form.documentTitleField'
              }}
              validationRules={{ maxLength: 150 }}
              InputProps={{
                endAdornment: (
                  <Typography sx={{ opacity: 0.6 }} component="span">
                    {fileExtension}
                  </Typography>
                )
              }}
              size="small"
            />
            <Button type="submit" sx={{ flexShrink: 0 }} variant="text">
              {t('common:labelSave')}
            </Button>
          </FormControllerTemp>
        ) : (
          <Tooltip title={t('common:labelView')}>
            <Button
              sx={{
                textTransform: 'none',
                color: isViewPrint ? 'inherit' : 'link.main',
                p: 0.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%'
              }}
              component="a"
              data-cy="document-view-button"
              {...(isPdf && { onClick: handleOpenDialog })} // Open PDF in dialog
              {...(!isPdf && {
                href: isUrl ? document.attachedFile.uri : `/documents/${document.id}`,
                target: '_blank',
                rel: 'noopener'
              })} // open link or non PDF document in new tab
              disabled={isNotPdfOrUrl && isDocumentDownloadBlocked}
            >
              {isUrl ? (
                <OpenInNewIcon />
              ) : (
                <RenderDocumentIcon fileType={document.attachedFile.format} sx={{ opacity: 1 }} />
              )}
              <Box
                sx={{
                  ml: 1,
                  maxWidth: '500px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '16px'
                }}
              >
                {document?.attachedFile?.name || document?.attachedFile?.uri}
              </Box>
            </Button>
          </Tooltip>
        )}

        {!isEditingTitle &&
          isTitleEditable &&
          isDeletable && ( // isDeletable = isEditable
            <IconButton
              onClick={() => setIsEditingTitle(true)}
              data-cy="document-edit-button"
              disabled={isProcessingOtherDocuments}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        {isDeletable && (
          <Tooltip title={t('common:labelDelete')}>
            <IconButton
              onClick={showDeleteConfirmationDialog ? handleConfirmDeleteDocument : handleDeleteDocument}
              data-cy="document-delete-button"
              disabled={isProcessingOtherDocuments}
              size="small"
              sx={{ bottom: isEditingTitle ? '-3px' : '' }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {isDocumentDownloadBlocked && isNotPdfOrUrl && (
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.6, lineHeight: 1 }}>
          {t('common:downloadBlockedDocumentMessage')}
        </Typography>
      )}
    </>
  );
};
